import { useCallback, useRef } from "react";

import { useOutletContext } from "@zenith/context";
import { OutletSelection } from "@zenith/context/global-context/types";
import {
  OutletApiService,
  OutletListDTO,
  PermittedOutletDTO,
} from "@zenith/services/user";

import { toOutletDisplayName } from "@zenith/features/OutletManagement/utils/outlet-display-name";

const toOutlet = (outlet: PermittedOutletDTO): OutletListDTO => ({
  id: outlet.id,
  name: toOutletDisplayName(outlet.name),
  address: outlet.address,
  location: outlet.location,
  status: outlet.status && OutletListDTO.status[outlet.status],
  isDefault: outlet.isDefault,
  isCurrent: outlet.isCurrent,
  balance: outlet.balance,
  availableBalance: outlet.availableBalance,
  ledgerBalance: outlet.ledgerBalance,
  outletImage: outlet.outletImage,
  wallet: outlet.walletId,
});

const singleOutletInView = (
  selection: OutletSelection | null,
  outlets: OutletListDTO[]
): OutletListDTO | undefined =>
  selection?.kind === "outlets" && selection.ids.length === 1
    ? outlets.find((outlet) => outlet.id === selection.ids[0])
    : undefined;

/**
 * What the outlet page needs from GET /api/v1/outlets beyond the permitted
 * list: where each outlet sits in the merchant's list, and the image the
 * merchant uploaded for it. The outlet page shows the list in that order,
 * while the permitted outlets come back in an order of their own, so the
 * switcher has to take its ordering from the same list to stay in step with
 * the outlet page. Both are extras, so a failure leaves the permitted data
 * untouched.
 */
type OutletListDetails = {
  order: Map<string, number>;
  images: Map<string, string>;
};

const loadOutletListDetails = async (): Promise<OutletListDetails> => {
  try {
    const response = await OutletApiService.listOutlets();
    const outlets = response.data ?? [];
    return {
      order: new Map(outlets.map((outlet, index) => [outlet.id ?? "", index])),
      // Only outlets the merchant gave an image to carry the field at all.
      images: new Map(
        outlets
          .filter((outlet) => outlet.outletImage)
          .map((outlet) => [outlet.id ?? "", outlet.outletImage as string])
      ),
    };
  } catch (error) {
    console.error("Failed to fetch the outlet list details:", error);
    return { order: new Map(), images: new Map() };
  }
};

const rankOf = (order: Map<string, number>, outlet: OutletListDTO): number =>
  order.get(outlet.id ?? "") ?? Number.MAX_SAFE_INTEGER;

/**
 * Copies each outlet's balances from `source` onto the outlet with the same id.
 */
const withBalancesFrom = <T extends OutletListDTO | PermittedOutletDTO>(
  outlets: OutletListDTO[],
  source: T[]
): OutletListDTO[] => {
  const byId = new Map(source.map((outlet) => [outlet.id, outlet]));
  return outlets.map((outlet) => {
    const priced = byId.get(outlet.id);
    return priced
      ? {
          ...outlet,
          balance: priced.balance,
          availableBalance: priced.availableBalance,
          ledgerBalance: priced.ledgerBalance,
        }
      : outlet;
  });
};

/**
 * Loads the outlets the user may act on, owned or assigned, into the outlet
 * context, then their balances. Balances take a CBS call per outlet, so they
 * follow the list instead of holding up the screens that only need the
 * outlets. A single outlet already in view is signed into first, so the server
 * acts on the outlet the screens show.
 */
export const useLoadOutlets = () => {
  const { outletContext, setOutlets, setOutletContext } = useOutletContext();
  const selection = useRef(outletContext.selection);
  selection.current = outletContext.selection;
  const loadedOutlets = useRef(outletContext.outlets);
  loadedOutlets.current = outletContext.outlets;

  const loadBalances = useCallback(() => {
    OutletApiService.getPermittedOutlets(true)
      .then((response) => {
        // Nothing to add them to once the context has been cleared meanwhile.
        if (loadedOutlets.current.length === 0) return;
        setOutlets(
          withBalancesFrom(loadedOutlets.current, response.data?.outlets ?? [])
        );
      })
      .catch((error) =>
        console.error("Failed to fetch outlet balances:", error)
      )
      .finally(() => setOutletContext({ isLoadingBalances: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOutlets]);

  return useCallback(async (): Promise<OutletListDTO[]> => {
    const [response, details] = await Promise.all([
      OutletApiService.getPermittedOutlets(false),
      loadOutletListDetails(),
    ]);
    // Balances already on screen stay there until the fresh ones arrive.
    let outlets = withBalancesFrom(
      (response.data?.outlets ?? [])
        .map(toOutlet)
        // The permitted list is not the endpoint that returns the uploaded
        // image, so fall back to the one that is.
        .map((outlet) => ({
          ...outlet,
          outletImage:
            outlet.outletImage ?? details.images.get(outlet.id ?? ""),
        }))
        .sort((a, b) => rankOf(details.order, a) - rankOf(details.order, b)),
      loadedOutlets.current
    );

    const inView = singleOutletInView(selection.current, outlets);
    if (inView?.id && !inView.isCurrent) {
      try {
        await OutletApiService.switchOutlet({ outletId: inView.id });
        outlets = outlets.map((outlet) => ({
          ...outlet,
          isCurrent: outlet.id === inView.id,
        }));
      } catch (error) {
        console.error("Failed to switch outlet:", error);
      }
    }

    setOutlets(outlets);
    loadBalances();
    return outlets;
  }, [setOutlets, loadBalances]);
};
